import {useCallback, useEffect, useLayoutEffect, useRef} from "react";
import {useHistory, useLocation} from "react-router-dom";

import {getPageRestoreDescriptor, savePageSnapshot, takeEntrySnapshot, takePageSnapshot} from "helpers/backNavigation";

const SCROLL_RESTORE_ATTEMPTS = 12;
const SCROLL_RESTORE_DELAY = 60;

const getMaxScrollY = () => {
    const documentHeight = Math.max(
        document.body?.scrollHeight || 0,
        document.documentElement?.scrollHeight || 0,
    );

    return Math.max(documentHeight - window.innerHeight, 0);
};

const restoreWindowScroll = (scrollY, attemptsLeft = SCROLL_RESTORE_ATTEMPTS) => {
    const maxScrollY = getMaxScrollY();
    const targetScrollY = Math.min(scrollY, maxScrollY);

    window.scrollTo(0, targetScrollY);

    if (attemptsLeft <= 0 || maxScrollY >= scrollY) {
        return;
    }

    window.setTimeout(() => {
        restoreWindowScroll(scrollY, attemptsLeft - 1);
    }, SCROLL_RESTORE_DELAY);
};

export const usePageRestore = ({ pageKey, getState, restoreState, watch, isReady = true, enabled = true }) => {
    const history = useHistory();
    const location = useLocation();
    const snapshotRef = useRef(null);
    const latestStateRef = useRef(null);
    const isRestoringRef = useRef(false);
    const canSaveRef = useRef(false);
    let watchSignature = null;

    if (typeof watch !== "undefined") {
        try {
            watchSignature = JSON.stringify(watch);
        } catch (error) {
            watchSignature = null;
        }
    }

    latestStateRef.current = getState ? getState() : null;

    const saveSnapshot = useCallback(() => {
        if (!enabled || !pageKey || !canSaveRef.current) {
            return;
        }

        savePageSnapshot({
            pageKey,
            locationKey: location.key,
            snapshot: {
                state: latestStateRef.current,
                scrollY: window.scrollY,
            },
        });
    }, [enabled, location.key, pageKey]);

    useEffect(() => {
        if (!enabled || !pageKey || snapshotRef.current) {
            return;
        }

        const restoreDescriptor = getPageRestoreDescriptor(location.state);
        const shouldRestoreFromFallback = restoreDescriptor?.restoreKey === pageKey;
        const shouldRestoreFromHistory = history.action === "POP";

        if (!shouldRestoreFromFallback && !shouldRestoreFromHistory) {
            canSaveRef.current = true;

            return;
        }

        const snapshot = shouldRestoreFromFallback
            ? takePageSnapshot({ pageKey })
            : takeEntrySnapshot({ pageKey, locationKey: location.key });

        if (!snapshot) {
            canSaveRef.current = true;

            return;
        }

        snapshotRef.current = snapshot;
        isRestoringRef.current = true;

        if (snapshot.state && restoreState) {
            restoreState(snapshot.state);
        }
    }, [enabled, history.action, location.key, location.state, pageKey, restoreState]);

    useEffect(() => {
        if (!enabled || !pageKey || !snapshotRef.current || !isReady) {
            return;
        }

        if (typeof snapshotRef.current.scrollY === "number") {
            restoreWindowScroll(snapshotRef.current.scrollY);
        }

        canSaveRef.current = true;
        isRestoringRef.current = false;
        snapshotRef.current = null;
        window.setTimeout(() => {
            saveSnapshot();
        }, 0);
    }, [enabled, isReady, location.key, location.pathname, pageKey, saveSnapshot]);

    useEffect(() => {
        if (!enabled || !pageKey) {
            return;
        }

        saveSnapshot();
    }, [enabled, pageKey, saveSnapshot, watchSignature]);

    useEffect(() => {
        if (!enabled || !pageKey) {
            return;
        }

        let saveTimeoutId = null;

        const handleScroll = () => {
            if (saveTimeoutId) {
                window.clearTimeout(saveTimeoutId);
            }

            saveTimeoutId = window.setTimeout(() => {
                saveSnapshot();
            }, 80);
        };

        window.addEventListener("scroll", handleScroll, { passive: true });

        return () => {
            if (saveTimeoutId) {
                window.clearTimeout(saveTimeoutId);
            }

            window.removeEventListener("scroll", handleScroll);
        };
    }, [enabled, pageKey, saveSnapshot]);

    useLayoutEffect(() => {
        if (!enabled || !pageKey) {
            return undefined;
        }

        return () => {
            if (!isRestoringRef.current) {
                saveSnapshot();
            }
        };
    }, [enabled, pageKey, saveSnapshot]);

    return {
        saveSnapshot,
    };
};
